import { useEffect, useState, useRef } from "react";
import { Modal, Form, Input, InputNumber, Select, Button, DatePicker, TimePicker, Checkbox, AutoComplete, Typography, List, Space, Tag, Tooltip } from "antd";
import { EditOutlined, DeleteOutlined, UserAddOutlined, ClockCircleOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import AdminInviteEmployeeModal from "./AdminInviteEmployeeModal";
import AdminEditRequestModal from "./AdminEditRequestModal";
import AdminConfirmRemovalModal from "./AdminConfirmRemovalModal";
import { useOuberService } from "../../../../services";
import { showErrorToast } from "../../ToastNotification";
import { getUnitAcronym, getActiveEarliestUnit } from "../../../../utils/ouberUtils";

dayjs.extend(customParseFormat);

const { Option } = Select;
const { Text } = Typography;

const getStatusTagClass = (status) => {
  switch (status) {
    case "Approved by AO":
    case "Created by AO":
    case "Approved": return "tag-approved";
    case "Requested to Join":
    case "Pending": return "tag-pending";
    case "Declined by AO":
    case "Declined":
    case "Cancelled by AO":
    case "Cancelled by User":
    case "Cancelled": return "tag-cancelled";
    default: return "";
  }
};

const AdminAddTripModal = ({ open, onCancel, onSave }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { service: ouberService } = useOuberService();
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isEditRequestOpen, setIsEditRequestOpen] = useState(false);
  const [isConfirmRemoveOpen, setIsConfirmRemoveOpen] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [passengers, setPassengers] = useState([]);
  // No predefined venues — admins type their own pickup/dropoff locations.
  const [existingVenues] = useState([]);

  const ouberServiceRef = useRef(ouberService);
  useEffect(() => {
    ouberServiceRef.current = ouberService;
  }, [ouberService]);

  useEffect(() => {
    if (open) {
      setLoading(false);
      form.resetFields();
      setPassengers([]);
      form.setFieldsValue({
        isMultiplePickupAllowed: false,
        deadlineTime: dayjs("11:59 pm", "h:mm a"),
        vehicleType: "University Owned",
      });

      const fetchRegistries = async () => {
        try {
          // Fetch drivers and vehicles in parallel
          const [drvs, vehs] = await Promise.all([
            ouberServiceRef.current.getDrivers(),
            ouberServiceRef.current.getVehicles()
          ]);
          
          setDrivers(Array.isArray(drvs) ? drvs : (drvs?.data || []));
          setVehicles(Array.isArray(vehs) ? vehs : (vehs?.data || []));
        } catch (error) {
          console.error("Failed to fetch drivers or vehicles", error);
        }
      };
      fetchRegistries();

    }
  }, [open, form]);

  const sanitizeInput = (val) => {
    if (typeof val !== 'string') return val;
    return val.trim().replace(/\s+/g, ' ');
  };

  const handleSubmit = async (values) => {
    const formattedValues = {
      trip_date: values.date ? values.date.format("YYYY-MM-DD") : null,
      pickup_time: values.time ? values.time.format("HH:mm:ss") : null,
      estimated_arrival_time: values.estimatedArrival ? values.estimatedArrival.format("HH:mm:ss") : null,
      pickup_venue: sanitizeInput(values.pickup),
      dropoff_location: sanitizeInput(values.destination),
      allowed_multiple_pickup: values.isMultiplePickupAllowed ? 1 : 0,
      trip_passenger_capacity: values.capacity,
      trip_purpose: sanitizeInput(values.purpose),
      remarks: sanitizeInput(values.purpose),
      driver_id: values.isCustomDriver ? null : (values.driver_id || null), 
      vehicle_id: values.isCustomVehicle ? null : (values.vehicle_id || null), 
      driver_contact: !values.isCustomDriver ? sanitizeInput(values.contactNumber) : null,
      vehicle_type: !values.isCustomVehicle ? sanitizeInput(values.vehicleType) : null,
      custom_driver_name: values.isCustomDriver ? sanitizeInput(values.custom_driver_name) : null,
      custom_driver_contact: values.isCustomDriver ? sanitizeInput(values.customNumber) : null,
      custom_vehicle_name: values.isCustomVehicle ? sanitizeInput(values.custom_vehicle_name) : null,
      custom_vehicle_plate: values.isCustomVehicle ? sanitizeInput(values.custom_vehicle_plate) : null,
      custom_vehicle_type: values.isCustomVehicle ? sanitizeInput(values.vehicleType) : null,
      passengers: passengers.map(p => ({
        ...p,
        name: sanitizeInput(p.name),
        email: sanitizeInput(p.email),
        pickup_venue: sanitizeInput(p.pickup_venue)
      })),
      deadline_date: values.deadlineDate ? values.deadlineDate.format("YYYY-MM-DD") : null,
      deadline_time: values.deadlineTime ? values.deadlineTime.format("HH:mm:ss") : null,
    };

    setLoading(true);
    try {
      if (onSave) {
        await onSave(formattedValues);
      }
      form.resetFields();
      setPassengers([]);
    } catch (e) {
      // Handled by parent
    } finally {
      setLoading(false);
    }
  };

  const handleSetHead = (passengerKey) => {
    setPassengers(prev => {
      const target = prev.find(p => (p.id || p.employee_id) === passengerKey);
      if (!target || target.is_external) return prev;
      return prev.map(p => ({
        ...p,
        is_head: (p.id || p.employee_id) === passengerKey,
      }));
    });
  };

  const hasHead = passengers.some(p => p.is_head);
  const canSubmitPassengers = passengers.length > 0 && hasHead;

  const handleOpenEditPassenger = (passenger) => {
    setSelectedPassenger(passenger);
    setIsEditRequestOpen(true);
  };

  const handleOpenRemovePassenger = (passenger) => {
    setSelectedPassenger(passenger);
    setIsConfirmRemoveOpen(true);
  };

  const sortedPassengers = [...passengers].sort((a, b) => {
    if (!a.time) return 1;
    if (!b.time) return -1;
    return dayjs(a.time, "h:mm a").diff(dayjs(b.time, "h:mm a"));
  });

  return (
    <>
    <Modal
      open={open}
      title="Create New Trip"
      onCancel={onCancel}
      footer={null}
      className="ouber-modal white-close-icon"
      width={700}
      styles={{
        header: { backgroundColor: "#862633", padding: "16px 24px", borderRadius: "8px 8px 0 0", margin: "-20px -24px 0 -24px" },
        title: { color: "white", margin: 0 },
        body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden', margin: '0 -24px', padding: '12px 24px' }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <Form 
          form={form} 
          layout="vertical" 
          onFinish={handleSubmit}
        >
          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item name="date" label="Trip Date" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
              <DatePicker 
                style={{ width: '100%' }} 
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>
            <Form.Item
              name="capacity"
              label="Passenger Capacity"
              style={{ flex: 1 }}
              dependencies={['vehicle_id', 'isCustomVehicle']}
              rules={[
                { required: true, message: "Input cannot be empty" },
                { type: 'number', min: 1, message: "Must be at least 1" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (getFieldValue('isCustomVehicle') || !value) return Promise.resolve();
                    const selectedVehicle = vehicles.find(v => v.vehicle_id === getFieldValue('vehicle_id'));
                    if (selectedVehicle && value > selectedVehicle.max_passenger_capacity) {
                      return Promise.reject(new Error(`Cannot exceed the vehicle's capacity of ${selectedVehicle.max_passenger_capacity}.`));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <InputNumber
                min={1}
                precision={0}
                style={{ width: '100%' }}
                onKeyPress={(event) => {
                  if (!/[0-9]/.test(event.key)) {
                    event.preventDefault();
                  }
                }}
              />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item 
              name="time" 
              label="Pickup Time" 
              style={{ flex: 1 }} 
              rules={[{ required: true, message: "Input cannot be empty" }]}
            >
              <TimePicker use12Hours format="h:mm a" style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.time !== curr.time}
            >
              {({ getFieldValue }) => (
                <Form.Item 
                  name="estimatedArrival" 
                  label="Estimated Arrival" 
                  style={{ flex: 1 }} 
                  dependencies={['time']}
                  rules={[
                    { required: true, message: "Input cannot be empty" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const pickup = getFieldValue('time');
                        if (!value || !pickup) return Promise.resolve();
                        
                        const pMin = pickup.hour() * 60 + pickup.minute();
                        const aMin = value.hour() * 60 + value.minute();
                        
                        if (aMin > pMin) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Arrival must be after pickup time"));
                      },
                    }),
                  ]}
                >
                  <TimePicker 
                    use12Hours 
                    format="h:mm a" 
                    style={{ width: '100%' }} 
                    disabledTime={() => {
                      const pickup = getFieldValue('time');
                      if (!pickup) return {};
                      return {
                        disabledHours: () => Array.from({ length: pickup.hour() }, (_, i) => i),
                        disabledMinutes: (h) => h === pickup.hour() ? Array.from({ length: pickup.minute() }, (_, i) => i) : [],
                      };
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: '16px' }}>
            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.date !== curr.date}
            >
              {({ getFieldValue }) => (
                <Form.Item 
                  name="deadlineDate" 
                  label="Deadline Date" 
                  style={{ flex: 1 }} 
                  dependencies={['date']}
                  rules={[
                    { required: true, message: "Input cannot be empty" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const tripDate = getFieldValue('date');
                        if (!value || !tripDate || value.isBefore(tripDate, 'day') || value.isSame(tripDate, 'day')) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("Deadline cannot be after the trip date"));
                      },
                    }),
                  ]}
                >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    disabledDate={(current) => {
                      const tripDate = getFieldValue('date');
                      if (!tripDate) return current && current < dayjs().startOf('day');
                      return current && (current.isAfter(tripDate, 'day') || current < dayjs().startOf('day'));
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
            <Form.Item 
              noStyle 
              shouldUpdate={(prev, curr) => prev.deadlineDate !== curr.deadlineDate || prev.time !== curr.time}
            >
              {({ getFieldValue }) => (
                <Form.Item 
                  name="deadlineTime" 
                  label="Deadline Time" 
                  style={{ flex: 1 }} 
                  dependencies={['deadlineDate', 'date', 'time']}
                  rules={[
                    { required: true, message: "Input cannot be empty" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const deadlineDate = getFieldValue('deadlineDate');
                        const tripDate = getFieldValue('date');
                        const pickupTime = getFieldValue('time');
                        
                        if (!value || !deadlineDate || !tripDate || !pickupTime) {
                          return Promise.resolve();
                        }
                        
                        if (deadlineDate.isSame(tripDate, 'day')) {
                          const dMin = value.hour() * 60 + value.minute();
                          const pMin = pickupTime.hour() * 60 + pickupTime.minute();
                          
                          if (dMin >= pMin) {
                            return Promise.reject(new Error("Deadline must be before pickup time on the same date"));
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <TimePicker 
                    use12Hours 
                    format="h:mm a" 
                    style={{ width: '100%' }} 
                    disabledTime={() => {
                      const dDate = getFieldValue('deadlineDate');
                      const tDate = getFieldValue('date');
                      const pickup = getFieldValue('time');
                      if (!dDate || !tDate || !pickup || !dDate.isSame(tDate, 'day')) return {};
                      return {
                        disabledHours: () => {
                          const hours = [];
                          const startHour = pickup.minute() === 0 ? pickup.hour() : pickup.hour() + 1;
                          for (let i = startHour; i < 24; i++) hours.push(i);
                          return hours;
                        },
                        disabledMinutes: (h) => {
                          if (h === pickup.hour()) {
                            const mins = [];
                            for (let i = pickup.minute(); i < 60; i++) mins.push(i);
                            return mins;
                          }
                          return [];
                        }
                      };
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Form.Item name="isCustomDriver" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Driver not in registry?</Checkbox>
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isCustomDriver !== currentValues.isCustomDriver}
          >
            {({ getFieldValue }) => {
              const isCustomDriver = getFieldValue('isCustomDriver');
              return isCustomDriver ? (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="custom_driver_name" label="Driver's Name" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                    <Input placeholder="Enter driver name" />
                  </Form.Item>
                  <Form.Item 
                    name="customNumber" 
                    label="Contact Number" 
                    style={{ flex: 1 }}
                    rules={[
                      { required: true, message: "Input cannot be empty" }, 
                      { pattern: /^09\d{9}$/, message: "Must be 11 digits and start with 09" }
                    ]}
                  >
                    <Input placeholder="E.g. 09123456789" maxLength={11} />
                  </Form.Item>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="driver_id" label="Driver's Name" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select 
                      showSearch
                      placeholder="Select a driver..."
                      optionFilterProp="children"
                      onChange={(val) => {
                        const selectedDriver = drivers.find(d => d.driver_id === val);
                        if (selectedDriver) {
                          form.setFieldsValue({ contactNumber: selectedDriver.contact_number });
                        }
                      }}
                    >
                      {drivers.sort((a, b) => a.driver_name.localeCompare(b.driver_name)).map(d => (
                        <Option key={d.driver_id} value={d.driver_id}>{d.driver_name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item 
                    name="contactNumber" 
                    label="Contact Number" 
                    style={{ flex: 1 }}
                    rules={[
                      { required: true, message: "Input cannot be empty" }, 
                      { pattern: /^09\d{9}$/, message: "Must be 11 digits and start with 09" }
                    ]}
                  >
                    <Input placeholder="E.g. 09123456789" maxLength={11} disabled />
                  </Form.Item>
                </div>
              );
            }}
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <Form.Item name="isCustomVehicle" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Vehicle not in registry?</Checkbox>
            </Form.Item>
          </div>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.isCustomVehicle !== currentValues.isCustomVehicle}
          >
            {({ getFieldValue }) => {
              const isCustomVehicle = getFieldValue('isCustomVehicle');
              return isCustomVehicle ? (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="custom_vehicle_name" label="Vehicle Name" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                    <Input placeholder="Enter vehicle name" />
                  </Form.Item>
                  <Form.Item name="custom_vehicle_plate" label="Plate Number" style={{ flex: 1 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
                    <Input placeholder="Enter plate number" />
                  </Form.Item>
                  <Form.Item name="vehicleType" label="Vehicle Type" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select>
                      <Option value="University Owned">University Owned</Option>
                      <Option value="Car Rental">Car Rental</Option>
                      <Option value="Carpool">Carpool</Option>
                      <Option value="Outsourced">Outsourced</Option>
                    </Select>
                  </Form.Item>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Form.Item name="vehicle_id" label="Vehicle" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select 
                      showSearch
                      placeholder="Select a vehicle..."
                      optionFilterProp="children"
                      onChange={(val) => {
                        const selectedVehicle = vehicles.find(v => v.vehicle_id === val);
                        if (selectedVehicle) {
                          form.setFieldsValue({ 
                            vehicleType: selectedVehicle.vehicle_type,
                            capacity: selectedVehicle.max_passenger_capacity
                          });
                        }
                      }}
                    >
                      {vehicles.sort((a, b) => a.vehicle_model.localeCompare(b.vehicle_model)).map(v => {
                        const tooSmall = v.max_passenger_capacity < passengers.length;
                        return (
                          <Option key={v.vehicle_id} value={v.vehicle_id} disabled={tooSmall}>
                            {v.vehicle_model} ({v.plate_number}){tooSmall ? ` — capacity ${v.max_passenger_capacity}, too small for ${passengers.length} passengers` : ""}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                  <Form.Item name="vehicleType" label="Vehicle Type" style={{ flex: 1 }} rules={[{ required: true, message: "Input cannot be empty" }]}>
                    <Select disabled>
                      <Option value="University Owned">University Owned</Option>
                      <Option value="Car Rental">Car Rental</Option>
                      <Option value="Carpool">Carpool</Option>
                      <Option value="Outsourced">Outsourced</Option>
                    </Select>
                  </Form.Item>
                </div>
              );
            }}
          </Form.Item>

          <div style={{ display: 'flex', gap: '16px', marginBottom: '8px' }}>
            <Form.Item name="pickup" label="Pickup Venue" style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
              <AutoComplete
                options={existingVenues}
                placeholder="Select or type venue..."
                filterOption={(inputValue, option) =>
                  option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Form.Item>
            <Form.Item name="destination" label="Dropoff Location" style={{ flex: 1, marginBottom: 0 }} rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
              <AutoComplete
                options={existingVenues}
                placeholder="Select or type location..."
                filterOption={(inputValue, option) =>
                  option.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Form.Item>
          </div>
          
          <Form.Item name="isMultiplePickupAllowed" valuePropName="checked" style={{ marginBottom: '16px' }}>
            <Checkbox>Allow Multiple Pickup Venues</Checkbox>
          </Form.Item>

          <Form.Item name="purpose" label="Purpose of Trip" rules={[{ required: true, whitespace: true, message: "Input cannot be empty" }]}>
            <Input.TextArea rows={3} placeholder="Purpose" />
          </Form.Item>

          <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <Text strong>Passengers</Text>
              <Button 
                type="dashed" 
                icon={<UserAddOutlined />} 
                onClick={() => setIsInviteOpen(true)}
              >
                Invite Passengers
              </Button>
            </div>

            {passengers.length > 0 && (
              <Text type="secondary" style={{ display: 'block', fontSize: '12px', marginBottom: '8px' }}>
                Click the star to set the <strong>Head of the Party</strong> — exactly one passenger must be designated (the Admin who creates the trip is never added as a passenger).
              </Text>
            )}

            <List
              size="small"
              bordered
              dataSource={sortedPassengers}
              style={{ maxHeight: '200px', overflowY: 'auto' }}
              renderItem={passenger => {
                const rawUnit = getActiveEarliestUnit(passenger);
                const passengerKey = passenger.id || passenger.employee_id;

                return (
                  <List.Item
                    actions={[
                      <Tooltip title={passenger.is_external ? "Guests cannot be Head of the Party" : (passenger.is_head ? "Head of the Party" : "Set as Head of the Party")}>
                        <Button
                          type="text"
                          disabled={passenger.is_external}
                          icon={passenger.is_head ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                          onClick={() => handleSetHead(passengerKey)}
                        />
                      </Tooltip>,
                      <Button type="text" icon={<EditOutlined />} onClick={() => handleOpenEditPassenger(passenger)} />,
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleOpenRemovePassenger(passenger)} />
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <>
                          {passenger.name}
                          {!passenger.is_external && (
                            <Text type="secondary" style={{ fontWeight: 400, fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                              ({getUnitAcronym(rawUnit)})
                            </Text>
                          )}
                          <Tag className={getStatusTagClass(passenger.is_head ? "Created by AO" : passenger.status)} style={{ border: 'none', marginLeft: '8px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
                            {passenger.is_head ? "Created by AO" : passenger.status}
                          </Tag>
                          {passenger.is_head && (
                            <Tag color="gold" style={{ border: 'none', marginLeft: '4px', fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
                              Head of the Party
                            </Tag>
                          )}
                        </>
                      }
                      description={
                        <Space style={{ fontSize: '12px' }}>
                          <span><ClockCircleOutlined /> {passenger.time || "N/A"}</span>
                          {form.getFieldValue('isMultiplePickupAllowed') && passenger.pickup_venue && (
                            <span>• {passenger.pickup_venue}</span>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />

            {passengers.length === 0 && (
              <Text type="danger" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                At least one passenger is required, with one set as Head of the Party.
              </Text>
            )}
            {passengers.length > 0 && !hasHead && (
              <Text type="danger" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                Set one passenger as Head of the Party to continue.
              </Text>
            )}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "24px" }}>
            <Button onClick={onCancel} disabled={loading}>Cancel</Button>
            <Button type="primary" htmlType="submit" className="ouber-btn-primary" loading={loading} disabled={!canSubmitPassengers}>
              Confirm Add
            </Button>
          </div>
        </Form>
      </div>
    </Modal>

      <AdminInviteEmployeeModal
        open={isInviteOpen}
        onCancel={() => setIsInviteOpen(false)}
        showUnit={false}
        emailRequired={false}
        requirePhone={true}
        onAddEmployees={(emps) => {
          const capacity = form.getFieldValue('capacity') || 0;
          const currentCount = passengers.length;
          const available = capacity - currentCount;

          if (emps.length > available) {
            showErrorToast("Error", `You can only add ${available} more passengers to this trip.`);
            return;
          }

          const formattedEmps = emps.map(emp => {
            const name = emp.name || emp.full_name || `${emp.first_name || ""} ${emp.last_name || ""}`.trim() || "Unknown";
            return {
              ...emp,
              id: emp.id,
              employee_id: emp.is_external ? null : emp.id,
              name: name,
              status: 'Approved by AO',
              time: null,
              pickup_venue: form.getFieldValue('pickup') || '',
              is_external: !!emp.is_external
            };
          });
          setPassengers(prev => {
            const next = [...prev, ...formattedEmps];
            // Default the first non-guest passenger added to Head of the
            // Party, so the admin isn't forced to make an extra click in
            // the common case — they can still reassign it via the star
            // toggle. Guests are never eligible to be Head of the Party.
            const alreadyHasHead = prev.some(p => p.is_head);
            if (!alreadyHasHead) {
              const firstEligibleIdx = next.findIndex(p => !p.is_external);
              if (firstEligibleIdx !== -1) {
                return next.map((p, idx) => idx === firstEligibleIdx ? { ...p, is_head: true } : p);
              }
            }
            return next;
          });
          setIsInviteOpen(false);
        }}
        maxSelectable={form.getFieldValue('capacity') ? Math.max(0, Number(form.getFieldValue('capacity')) - passengers.length) : null}
        existingPassengerIds={passengers.map(p => p.id || p.employee_id)}
      />

      <AdminEditRequestModal 
        open={isEditRequestOpen}
        onCancel={() => setIsEditRequestOpen(false)}
        passengerData={selectedPassenger}
        isMultiplePickupAllowed={form.getFieldValue('isMultiplePickupAllowed')}
        onSave={(updatedData) => {
          setPassengers(passengers.map(p => {
            if ((p.id || p.employee_id) === (updatedData.id || updatedData.employee_id)) {
              return {
                ...p,
                ...updatedData,
                status: updatedData.request_status,
                time: updatedData.estimated_pickup_time ? dayjs(updatedData.estimated_pickup_time, "HH:mm:ss").format("h:mm a") : p.time,
                pickup_venue: updatedData.pickup_venue
              };
            }
            return p;
          }));
          setIsEditRequestOpen(false);
        }}
      />

      <AdminConfirmRemovalModal 
        open={isConfirmRemoveOpen}
        onCancel={() => setIsConfirmRemoveOpen(false)}
        passengerData={selectedPassenger}
        onConfirm={(data) => {
          setPassengers(passengers.filter(p => (p.id || p.employee_id) !== (data.id || data.employee_id)));
          setIsConfirmRemoveOpen(false);
        }}
      />
    </>
  );
};

export default AdminAddTripModal;
